import React, { useState, useRef } from 'react';
import { Upload, X, Image, Film, Loader2 } from 'lucide-react';

const FileUploader = ({ onUpload, accept = "image/*,video/*", multiple = false, label = "Dosya Seç" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setError('');
    
    try {
      if (multiple) {
        // Multiple file upload
        const formData = new FormData();
        Array.from(files).forEach(file => {
          formData.append('files', file);
        });
        
        const res = await fetch(`${backendUrl}/api/admin/upload/multiple`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          const successfulUploads = data.results.filter(r => r.success);
          successfulUploads.forEach(upload => {
            const fullUrl = `${backendUrl}${upload.url}`;
            onUpload(fullUrl);
          });
        } else {
          const errorData = await res.json();
          setError(errorData.detail || 'Yükleme başarısız');
        }
      } else {
        // Single file upload
        const file = files[0];
        
        // Show preview for images
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => setPreview(e.target.result);
          reader.readAsDataURL(file);
        } else if (file.type.startsWith('video/')) {
          setPreview('video');
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${backendUrl}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          const fullUrl = `${backendUrl}${data.url}`;
          onUpload(fullUrl);
          setPreview(null);
        } else {
          const errorData = await res.json();
          setError(errorData.detail || 'Yükleme başarısız');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Yükleme hatası');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div className="space-y-2">
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            <span className="text-sm text-gray-500">Yükleniyor...</span>
          </div>
        ) : preview ? (
          <div className="relative">
            {preview === 'video' ? (
              <div className="flex items-center justify-center py-4">
                <Film className="w-12 h-12 text-gray-400" />
              </div>
            ) : (
              <img src={preview} alt="Preview" className="max-h-32 mx-auto rounded" />
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreview(null);
              }}
              className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-xs text-gray-400">veya sürükle bırak</span>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default FileUploader;
